import { useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Badge } from '../../atoms/Badge/Badge';
import { Button } from '../../atoms/Button/Button';
import { Text } from '../../atoms/Typography/Text';
import { TableWrap } from '../../budget/BudgetPageParts';
import type { TestMapping, TestMappingTarget } from '../../../services/testMapping';
import { TestMappingDialog } from './TestMappingDialog';
import {
  NEW_PARAMETER, draftOf, draftTarget, formatDate, formatValue, parseScale, targetLabel, type MappingDraft, type MappingDrafts,
} from './testMappingDraft';
import './TestMapping.css';

export interface TestMappingTableProps {
  rows: readonly TestMapping[];
  targets: readonly TestMappingTarget[];
  drafts: MappingDrafts;
  /** The dialog's choice for one test; resolve to close the dialog. */
  onSubmitRow: (sourceKey: string, draft: MappingDraft) => Promise<void> | void;
  isSubmitting?: boolean;
  submitError?: boolean;
  onEditStart?: () => void;
  disabled?: boolean;
}

/**
 * Every test of a device (ReefBot, Mastertronic) in one table — like the controller's "Empfangene Werte":
 * the test, where it goes in ReefGuide, its latest value, the value as stored,
 * when it was tested, and "Bearbeiten" for the mapping dialog.
 */
export function TestMappingTable({
  rows, targets, drafts, onSubmitRow, isSubmitting = false, submitError = false, onEditStart, disabled,
}: TestMappingTableProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [editing, setEditing] = useState<TestMapping | null>(null);
  const byId = new Map(targets.map((target) => [target.parameterId, target]));
  const caption = t('integrations.testMapping.caption');

  return (
    <>
      <TableWrap label={caption}>
        <table className="test-mapping-table">
          <caption className="test-mapping-table__caption">{caption}</caption>
          <thead>
            <tr>
              <th scope="col">{t('integrations.testMapping.test')}</th>
              <th scope="col">{t('integrations.testMapping.target')}</th>
              <th scope="col" className="test-mapping-table__num">{t('integrations.testMapping.reported')}</th>
              <th scope="col" className="test-mapping-table__num">{t('integrations.testMapping.inReefGuide')}</th>
              <th scope="col">{t('integrations.testMapping.measuredAt')}</th>
              <th scope="col"><span className="test-mapping-table__sr">{t('integrations.testMapping.actions')}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = draftOf(row, drafts);
              const target = draftTarget(draft, byId);
              const scale = parseScale(draft.scale);
              const ignored = !target;
              let mapping: string;
              if (draft.parameterId === NEW_PARAMETER) {
                mapping = target
                  ? t('integrations.testMapping.newParameter', { name: target.unit ? `${target.parameterName} (${target.unit})` : target.parameterName })
                  : t('integrations.testMapping.customNameMissing');
              } else {
                mapping = target ? targetLabel(t, target) : t('integrations.testMapping.ignore');
              }
              if (target && scale !== null && scale !== 1) mapping += ` · × ${scale.toLocaleString(language)}`;

              return (
                <tr key={row.sourceKey} className={ignored ? 'test-mapping-table__row--ignored' : undefined}>
                  <th scope="row">
                    <span className="test-mapping-table__name">{row.sourceName}</span>
                    {row.status === 'new' && <Badge tone="info">{t('integrations.testMapping.new')}</Badge>}
                  </th>
                  <td className="test-mapping-table__mapping">{mapping}</td>
                  <td className="test-mapping-table__num">
                    {row.latestValue !== null ? formatValue(row.latestValue, row.reportedUnit, language) : '–'}
                  </td>
                  <td className="test-mapping-table__num">
                    {target && scale !== null && row.latestValue !== null ? formatValue(row.latestValue * scale, target.unit, language) : '–'}
                  </td>
                  <td className="test-mapping-table__date">
                    {row.latestMeasuredAt
                      ? <time dateTime={row.latestMeasuredAt}>{formatDate(row.latestMeasuredAt, language)}</time>
                      : <Text as="span" color="muted">{t('integrations.testMapping.noValue')}</Text>}
                  </td>
                  <td>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={disabled}
                      aria-label={t('integrations.testMapping.editFor', { name: row.sourceName })}
                      onClick={() => { onEditStart?.(); setEditing(row); }}
                    >
                      {t('integrations.testMapping.edit')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrap>

      {editing && (
        <TestMappingDialog
          row={editing}
          targets={targets}
          initial={draftOf(editing, drafts)}
          onCancel={() => setEditing(null)}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onSubmit={(draft) => {
            void Promise.resolve(onSubmitRow(editing.sourceKey, draft)).then(() => setEditing(null), () => undefined);
          }}
        />
      )}
    </>
  );
}
